import { useState, useCallback } from 'react'

const DELETABLE_PREFIX = '360_DELETABLE_'

/**
 * Hook to manage state that is persisted to localStorage or sessionStorage.
 * @param {string | null} key
 * @param {*} defaultValue
 * @param {Storage} storage
 * @param {boolean} clearOnLogout - Whether to clear this key on logout (default: true)
 * @returns
 */
export default function usePersistedState(key, defaultValue, storage = localStorage, clearOnLogout = true) {
  const storageKey = key && clearOnLogout ? `${DELETABLE_PREFIX}${key}` : key

  const [state, setState] = useState(() => {
    if (!storageKey) return defaultValue
    try {
      const saved = storage.getItem(storageKey)
      return saved ? JSON.parse(saved) : defaultValue
    } catch {
      return defaultValue
    }
  })

  const setValue = useCallback(
    /**
     * @param {* | ((prev: T) => T)} value
     */
    (value) => {
      const newValue = typeof value === 'function' ? value(state) : value

      setState(value)

      if (storageKey && newValue !== undefined && newValue !== null) {
        storage.setItem(storageKey, JSON.stringify(newValue))
      } else if (storageKey) {
        storage.removeItem(storageKey)
      }
    },
    [storageKey, storage, state]
  )

  return [state, setValue]
}

/**
 * Clears all keys with the deletable prefix. Call this on logout
 * so table filters and such aren't still present.
 */
export function clearPersistedState() {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(DELETABLE_PREFIX)) {
      localStorage.removeItem(key)
    }
  })

  Object.keys(sessionStorage).forEach((key) => {
    if (key.startsWith(DELETABLE_PREFIX)) {
      sessionStorage.removeItem(key)
    }
  })
}
